import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Linking, SafeAreaView, StyleSheet } from 'react-native';
import WebView from 'react-native-webview';
import { WebViewOpenWindowEvent } from 'react-native-webview/lib/WebViewTypes';
import { featureCountryCode } from 'states/persistInStorage';
import { CountryCode, Lang } from 'types/generatedSchemaTypes';
import { getLang } from 'utils/commonUtils';
import { isWeb } from 'utils/deviceUtils';
import { parseFeatureFlagObject } from 'utils/featureFlagUtils';

// Can be enabled if needed
// const checkAndClickChatButton = `
//   const chatButtonInterval = setInterval(() => {
//     const chatButton = document.querySelector('.sf-new-bubble');
//     if (chatButton) {
//       chatButton.click();
//       clearInterval(chatButtonInterval);
//     }
//   }, 250);
// `;

const checkCookiesEnabled = `
  function findButtonInShadowDOM(root) {
    // Handle shadow root traversal
    if (root.shadowRoot) {
      const button = root.shadowRoot.querySelector('button');
      if (button) return button;

      // Search through shadow root children
      const elements = root.shadowRoot.querySelectorAll('*');
      for (const element of elements) {
        const found = findButtonInShadowDOM(element);
        if (found) return found;
      }
    }

    // Search through regular DOM children
    const elements = root.querySelectorAll('*');
    for (const element of elements) {
      const found = findButtonInShadowDOM(element);
      if (found) return found;
    }
    return null;
  }

  // Start search from document root
  setTimeout(() => {
    const chatContainer = document.querySelector('.sf-chat-container');
    const button = findButtonInShadowDOM(document.body);
    if (button && !chatContainer || chatContainer.style.display === 'none') {
      button.click();
    }
  }, 2000);
`;

const injectedJavaScript = `
  document.querySelector('.aimo-header').style.display = 'none';
  document.querySelector('.aimo-footer').style.display = 'none';
  document.querySelector('.contentpage__heading').style.display = 'none';
  document.querySelector('.contentpage__content-area').style.display = 'none';

  const h2Elements = document.querySelectorAll('h2');
  h2Elements.forEach(h2 => h2.remove());

  ${checkCookiesEnabled}
  true;
`;

const getUrl = (
  urls: Record<CountryCode, Record<Lang, string>>,
  country: CountryCode,
  language: Lang
): string => {
  const countryUrls = urls[country] ?? urls['FI']; // Fallback to FI
  const url = countryUrls[language] ?? countryUrls['en']; // Fallback to English
  return url;
};

const Chatbot = () => {
  const webViewRef = useRef<WebView>(null);
  const {
    flags: { chatbot_urls },
  } = useContext(FeatureFlagsContext);
  const featureCountry = useReactiveVar(featureCountryCode) ?? 'FI';

  const urls = useMemo(() => {
    const urlData = parseFeatureFlagObject<
      Record<CountryCode, Record<Lang, string>>
    >(chatbot_urls, 'chatbot_urls');
    return urlData;
  }, [chatbot_urls]);

  const url = getUrl(urls, featureCountry, getLang());

  const { goBack } = useNavigation();
  const handleCreateWindow = (event: WebViewOpenWindowEvent) => {
    Linking.openURL(event.nativeEvent.targetUrl);
    return false;
  };

  const handleNavigate = (event: any) => {
    if (!event?.url?.includes(url)) {
      Linking.openURL(event.url);
      webViewRef.current?.goBack();
    }
  };

  useEffect(() => {
    if (isWeb) {
      goBack();
      (window as any).open(url, '_blank');
    }
  }, [isWeb, url, featureCountry, goBack]);

  return (
    <SafeAreaView style={styles.container}>
      <WebView
        ref={webViewRef}
        source={{ uri: url }}
        style={styles.webview}
        javaScriptEnabled
        domStorageEnabled
        injectedJavaScript={injectedJavaScript}
        onOpenWindow={handleCreateWindow}
        onNavigationStateChange={handleNavigate}
        //Needed for injecting JS into the webview
        onMessage={(_) => _}
        useWebView2
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  webview: {
    flex: 1,
  },
});

export default Chatbot;
